import React, { memo, useMemo, useState } from 'react';
import { useConversationStarters } from '../../utils/hooks';
import { PageContext } from '../hooks/usePageContext';
import { TextBox, SuggestionListWidget, useWidgetConfig } from '@brandclub/bc-widgets';
import type { SuggestionListWidgetState } from '@brandclub/bc-widgets';
import { useIsMobile } from './ConciergeChatAreaV2/useIsMobile';

/**
 * Starter suggestion pills in the EMBEDDED welcome (the chat bubble / panel).
 *
 * Hidden on phone-sized viewports: the bubble was stacking a product
 * card, the greeting, four starter pills and the composer into one sheet, and
 * with the keyboard open the pills pushed the input out of reach. The
 * full-page `/concierge` welcome keeps its own pills. Desktop embedded panels
 * still render the asset-backed product starter questions.
 */
const EMBEDDED_SUGGESTION_PILLS_ENABLED: boolean = true;

/**
 * Arrow/redirect icon shown as a prefix in suggestion pills.
 * Uses the exact SVG path from the Figma mock.
 */
const ArrowIcon: React.FC<{ color?: string }> = ({ color = 'currentColor' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="12"
    height="14"
    viewBox="0 0 10 12"
    fill="none"
    style={{ flexShrink: 0 }}
  >
    <path
      d="M9.2697 7.16486C9.27038 7.1669 9.27106 7.16893 9.27173 7.17097C9.28997 7.21715 9.30078 7.26673 9.30078 7.31971C9.30078 7.372 9.28997 7.42226 9.27173 7.46844C9.27106 7.47048 9.27038 7.47252 9.2697 7.47455C9.24876 7.52481 9.21904 7.57031 9.1812 7.60835L6.12418 10.6809C5.9661 10.8398 5.70939 10.8398 5.55129 10.6809C5.39321 10.5219 5.39321 10.2639 5.55129 10.1049L7.91652 7.72718H3.94894C1.93704 7.72718 0.300781 6.08228 0.300781 4.05971V0.707551C0.300781 0.482746 0.482514 0.300049 0.706136 0.300049C0.929757 0.300049 1.11149 0.482746 1.11149 0.707551V4.05971C1.11149 5.63263 2.38428 6.91211 3.94887 6.91211H7.91594L5.55071 4.53436C5.39262 4.37544 5.39262 4.11736 5.55071 3.95844C5.62975 3.87897 5.73379 3.8389 5.83714 3.8389C5.94051 3.8389 6.04454 3.87897 6.12358 3.95844L9.1806 7.03164C9.21911 7.06899 9.24876 7.11392 9.2697 7.16486Z"
      fill={color}
      stroke={color}
      strokeWidth="0.6"
    />
  </svg>
);

/** Map font-weight keywords to CSS numeric values */
const fontWeightMap: Record<string, number> = {
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
};

/**
 * Individual suggestion pill with hover state management.
 * Renders an optional arrow icon prefix based on widget state.
 */
const SuggestionPill: React.FC<{
  query: string;
  state: SuggestionListWidgetState;
  resolveColor: (color: any) => string;
  onSearch: (searchTerm: string) => void;
}> = ({ query, state, resolveColor, onSearch }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <button
      onClick={() => onSearch(query)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        padding: `${state.pillPaddingY}px ${state.pillPaddingX}px`,
        borderRadius: `${state.pillBorderRadius}px`,
        border: `${state.pillBorderWidth}px solid ${resolveColor(state.pillBorderColor)}`,
        backgroundColor: isHovered
          ? resolveColor(state.pillBgColorHover) || resolveColor(state.pillBgColor)
          : resolveColor(state.pillBgColor),
        color: resolveColor(state.pillTextColor),
        fontSize: `${state.pillFontSize}px`,
        fontWeight: fontWeightMap[state.pillFontWeight] || 500,
        fontFamily: state.pillFontFamilyCss || 'inherit',
        cursor: 'pointer',
        textAlign: 'left',
        boxShadow: '0 4px 16px 0 rgba(0, 0, 0, 0.05)',
        transition: 'background-color 0.15s ease',
        width: state.layout === 'horizontal' ? 'auto' : '100%',
        whiteSpace: state.layout === 'horizontal' ? 'nowrap' : undefined,
        flexShrink: state.layout === 'horizontal' ? 0 : undefined,
        display: 'flex',
        alignItems: 'center',
        gap: state.showIcon ? '8px' : undefined,
      }}
    >
      {state.showIcon && <ArrowIcon color={resolveColor(state.pillTextColor)} />}
      {query}
    </button>
  );
};

interface ConciergeWelcomeEmbeddedProps {
  displayQueries: string[];
  isLoadingSuggestedQueries: boolean;
  onSearch: (searchTerm: string) => void;
  pageContext?: PageContext;
  apiBaseUrl?: string;
  /** User's first name for personalized greeting */
  userName?: string;
  // Props accepted from ConciergeWelcome but unused in the embedded layout
  brandLogoUrl?: string;
  brandName?: string;
  conciergeName?: string;
  searchInputValue?: string;
  setSearchInputValue?: (value: string) => void;
  handleSearch?: (searchTerm: string, files?: Array<{ type: 'file'; mediaType: string; url: string }>) => void;
  isActivelyStreaming?: boolean;
}

const ConciergeWelcomeEmbeddedComponent: React.FC<ConciergeWelcomeEmbeddedProps> = ({
  displayQueries,
  isLoadingSuggestedQueries,
  onSearch,
  pageContext,
  apiBaseUrl,
  userName,
  brandName = '',
  conciergeName = 'Concierge',
}) => {
  const pageType = pageContext?.pageType;
  const stacklineSku = pageContext?.stacklineSku;
  // Shared with the rest of Concierge (same 768px the widget itself uses to pick
  // sheet-vs-panel) and re-reads on resize/rotation, which an inline
  // `window.innerWidth` read at render time does not.
  const isMobileViewport = useIsMobile();

  const greetingConfig = useWidgetConfig<{ loggedOutGreeting?: string }>('concierge-welcome-greeting', {});

  // Fetch context-specific queries using the shared hook
  const { questions: contextQueries, isLoading: isLoadingContextQueries } = useConversationStarters({
    stacklineSku,
    apiBaseUrl,
    source: pageContext?.source,
    enabled: pageType === 'pdp' && !!stacklineSku,
  });

  // Use context queries if available, otherwise fall back to displayQueries
  const queriesToShow = useMemo(
    () => (contextQueries.length > 0 ? contextQueries : displayQueries),
    [contextQueries, displayQueries],
  );
  const isLoading = useMemo(
    () => isLoadingContextQueries || (pageContext?.pageType === 'pdp' && isLoadingSuggestedQueries),
    [isLoadingContextQueries, pageContext?.pageType, isLoadingSuggestedQueries],
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        flex: 1,
        minHeight: 0,
        boxSizing: 'border-box',
      }}
    >
      {/* Welcome text - vertically centered in the available space above pills */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px 16px 0 16px',
          textAlign: 'center',
          minHeight: 0,
          gap: 0,
        }}
      >
        {/* Greeting line (e.g. "Hi, Lindsey.") */}
        <TextBox
          widgetId="concierge-welcome-greeting"
          displayName="Welcome Greeting"
          initialState={{
            content: userName ? `Hi, ${userName}.` : (greetingConfig.loggedOutGreeting || 'Hello!'),
            fontSize: 16,
            fontWeight: 'semibold',
            color: {
              type: 'custom',
              value: '#000000',
            },
            textAlign: 'center',
            lineHeight: 1.625,
            letterSpacing: 0.08,
            textTransform: 'none',
          }}
        />
        {/* Main message line (e.g. "I'm Concierge, your Brandclub shopping assistant." or "I'm Ali, your shopping assistant for Cheerios.") */}
        <div style={{ maxWidth: 340 }}>
          <TextBox
            widgetId="concierge-welcome-message"
            displayName="Welcome Message"
            excludeEditFields={['content']}
            initialState={{
              content:
                brandName && brandName.toLowerCase() !== 'brandclub'
                  ? `I'm ${conciergeName}, your shopping assistant for ${brandName}.`
                  : `I'm ${conciergeName}, your Brandclub shopping assistant.`,
              fontSize: 20,
              fontWeight: 'semibold',
              color: {
                type: 'custom',
                value: '#000000',
              },
              textAlign: 'center',
              lineHeight: 1.4,
              letterSpacing: 0.08,
              textTransform: 'none',
            }}
          />
        </div>
      </div>

      {/* Suggestion pills - anchored at the bottom, just above the input area */}
      {EMBEDDED_SUGGESTION_PILLS_ENABLED && !isMobileViewport && queriesToShow.length > 0 && !isLoading && (
        <SuggestionListWidget widgetId="concierge-suggestion-list" displayName="Suggestion List">
          {(state, resolveColor) => {
            const isHorizontal = state.layout === 'horizontal';
            return (
              <div
                style={{
                  flexShrink: 0,
                  padding: '0 16px 12px 16px',
                }}
              >
                <div
                  style={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: isHorizontal ? 'row' : 'column',
                    gap: `${state.containerGap}px`,
                    maxWidth: '100%',
                    ...(isHorizontal
                      ? {
                          overflowX: 'auto',
                          overflowY: 'hidden',
                          paddingRight: 16,
                          paddingBottom: 4,
                          scrollbarWidth: 'none' as const,
                          msOverflowStyle: 'none' as const,
                        }
                      : {}),
                  }}
                >
                  {queriesToShow.slice(0, isHorizontal ? undefined : 4).map((query, index) => (
                    <SuggestionPill
                      key={index}
                      query={query}
                      state={state}
                      resolveColor={resolveColor}
                      onSearch={onSearch}
                    />
                  ))}
                </div>
                {/* Hide scrollbar for horizontal layout */}
                {isHorizontal && (
                  <style>{`
                    .concierge-root div::-webkit-scrollbar { display: none; }
                  `}</style>
                )}
              </div>
            );
          }}
        </SuggestionListWidget>
      )}
    </div>
  );
};

// Memoize to prevent re-renders when parent re-renders due to input changes
export const ConciergeWelcomeEmbedded = memo(ConciergeWelcomeEmbeddedComponent);
